/**
 * Step 5 — customer contact details + review.
 *
 * a11y:
 *   - `autocomplete="tel"` on phone and `autocomplete="email"` on email.
 *   - Error messages are associated to their input via `aria-describedby` +
 *     `aria-invalid="true"` on the field when the validation fails.
 *   - SMS opt-in checkbox is a real `<input type="checkbox">`.
 */

import { useMemo, useState, type Dispatch, type FormEvent } from 'react';
import type { BookingAction } from '../wizardState';
import type { BookingWizardState } from '../types';
import { Button } from '@/components/primitives';

export interface Step5YourDetailsProps {
  state: BookingWizardState;
  dispatch: Dispatch<BookingAction>;
  onSubmit: () => void;
  onBack: () => void;
}

function isValidEmail(s: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}
function isValidPhone(s: string): boolean {
  const digits = s.replace(/\D/g, '');
  return digits.length === 10 || digits.length === 11;
}

export function Step5YourDetails({ state, dispatch, onSubmit, onBack }: Step5YourDetailsProps): JSX.Element {
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const errors = useMemo<Record<string, string>>(() => {
    const errs: Record<string, string> = {};
    if (!state.firstName.trim()) errs['firstName'] = 'First name is required.';
    if (!state.lastName.trim()) errs['lastName'] = 'Last name is required.';
    if (!isValidEmail(state.email)) errs['email'] = 'Enter a valid email address.';
    if (!isValidPhone(state.phone)) errs['phone'] = 'Enter a 10-digit US phone number.';
    return errs;
  }, [state]);

  const isValid = Object.keys(errors).length === 0;

  function onFormSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setTouched({ firstName: true, lastName: true, email: true, phone: true });
    if (isValid) onSubmit();
  }

  function err(name: string): string | null {
    return touched[name] ? (errors[name] ?? null) : null;
  }

  return (
    <main className="rae-wizard__main">
      <button type="button" className="rae-wizard__back" onClick={onBack}>← Back</button>
      <h1 className="rae-wizard__h1">Your details</h1>
      <p className="rae-wizard__lead">Last step! Tell us who's coming so we can get ready for you.</p>

      <form onSubmit={onFormSubmit} noValidate>
        <section className="rae-card" aria-labelledby="rae-who-h2">
          <h2 id="rae-who-h2">Who is this appointment for?</h2>
          <div className="rae-form-row">
            <div className="rae-field">
              <label htmlFor="rae-first">First name</label>
              <input
                id="rae-first"
                type="text"
                value={state.firstName}
                autoComplete="given-name"
                required
                aria-invalid={err('firstName') ? 'true' : undefined}
                aria-describedby={err('firstName') ? 'rae-first-err' : undefined}
                onBlur={() => setTouched({ ...touched, firstName: true })}
                onChange={(e) => dispatch({ type: 'setDetails', details: { firstName: e.target.value } })}
              />
              {err('firstName') && (
                <div id="rae-first-err" className="rae-field__error" role="alert">
                  <span aria-hidden>⚠</span> {err('firstName')}
                </div>
              )}
            </div>
            <div className="rae-field">
              <label htmlFor="rae-last">Last name</label>
              <input
                id="rae-last"
                type="text"
                value={state.lastName}
                autoComplete="family-name"
                required
                aria-invalid={err('lastName') ? 'true' : undefined}
                aria-describedby={err('lastName') ? 'rae-last-err' : undefined}
                onBlur={() => setTouched({ ...touched, lastName: true })}
                onChange={(e) => dispatch({ type: 'setDetails', details: { lastName: e.target.value } })}
              />
              {err('lastName') && (
                <div id="rae-last-err" className="rae-field__error" role="alert">
                  <span aria-hidden>⚠</span> {err('lastName')}
                </div>
              )}
            </div>
          </div>
          <div className="rae-form-row">
            <div className="rae-field">
              <label htmlFor="rae-email">Email</label>
              <input
                id="rae-email"
                type="email"
                value={state.email}
                autoComplete="email"
                required
                inputMode="email"
                aria-invalid={err('email') ? 'true' : undefined}
                aria-describedby={err('email') ? 'rae-email-err' : undefined}
                onBlur={() => setTouched({ ...touched, email: true })}
                onChange={(e) => dispatch({ type: 'setDetails', details: { email: e.target.value } })}
              />
              {err('email') && (
                <div id="rae-email-err" className="rae-field__error" role="alert">
                  <span aria-hidden>⚠</span> {err('email')}
                </div>
              )}
            </div>
            <div className="rae-field">
              <label htmlFor="rae-phone">Mobile number</label>
              <input
                id="rae-phone"
                type="tel"
                value={state.phone}
                autoComplete="tel"
                required
                inputMode="tel"
                aria-invalid={err('phone') ? 'true' : undefined}
                aria-describedby={err('phone') ? 'rae-phone-err' : undefined}
                onBlur={() => setTouched({ ...touched, phone: true })}
                onChange={(e) => dispatch({ type: 'setDetails', details: { phone: e.target.value } })}
              />
              {err('phone') && (
                <div id="rae-phone-err" className="rae-field__error" role="alert">
                  <span aria-hidden>⚠</span> {err('phone')}
                </div>
              )}
            </div>
          </div>

          <div className="rae-check-row">
            <input
              id="rae-sms"
              type="checkbox"
              checked={state.smsReminders}
              onChange={(e) => dispatch({ type: 'setDetails', details: { smsReminders: e.target.checked } })}
              style={{ width: 18, height: 18, accentColor: 'var(--pcb-blue)' }}
            />
            <label htmlFor="rae-sms">
              <strong style={{ color: 'var(--pcb-blue-dark)' }}>Text me appointment reminders.</strong>
              <span style={{ display: 'block', fontSize: 12, color: 'var(--ink-500)', marginTop: 3 }}>
                Standard message rates may apply. You can opt out anytime by replying STOP.
              </span>
            </label>
          </div>
        </section>

        <section className="rae-card" aria-labelledby="rae-prep-h2">
          <h2 id="rae-prep-h2">Help us prepare <span style={{ fontSize: 13, color: 'var(--ink-500)', fontWeight: 400 }}>(optional)</span></h2>
          <div className="rae-form-row is-full">
            <div className="rae-field">
              <label htmlFor="rae-funding">Approximate funding amount <span className="opt">(optional)</span></label>
              <select
                id="rae-funding"
                value={state.fundingAmountRange}
                onChange={(e) => dispatch({ type: 'setDetails', details: { fundingAmountRange: e.target.value } })}
              >
                <option value="">Select a range</option>
                <option value="<1k">Less than $1,000</option>
                <option value="1k-5k">$1,000 – $5,000</option>
                <option value="5k-25k">$5,000 – $25,000</option>
                <option value="25k-100k">$25,000 – $100,000</option>
                <option value=">100k">More than $100,000</option>
                <option value="na">Prefer not to say</option>
              </select>
            </div>
          </div>
          <div className="rae-form-row is-full">
            <div className="rae-field">
              <label htmlFor="rae-notes">Anything else we should know? <span className="opt">(optional)</span></label>
              <textarea
                id="rae-notes"
                rows={3}
                value={state.notes}
                onChange={(e) => dispatch({ type: 'setDetails', details: { notes: e.target.value } })}
                placeholder="e.g. I'd like to compare this to an IRA as well"
              />
            </div>
          </div>
        </section>

        <section className="rae-review" aria-labelledby="rae-review-h2">
          <h2 id="rae-review-h2">Review your appointment</h2>
          <div className="rae-review__row"><span className="k">Service</span><span className="v">{state.service?.title} · {state.service?.durationMin} min</span></div>
          <div className="rae-review__row"><span className="k">When</span><span className="v">{state.date && state.time ? `${new Date(`${state.date}T12:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })} at ${state.time} CDT` : '—'}</span></div>
          <div className="rae-review__row"><span className="k">Where</span><span className="v">{state.branch?.branchName ?? '—'}</span></div>
          <div className="rae-review__row"><span className="k">Method</span><span className="v">{methodText(state)}</span></div>
          <div className="rae-review__row"><span className="k">For</span><span className="v">{state.firstName} {state.lastName}</span></div>
        </section>

        <div className="rae-wizard__actions">
          <Button variant="ghost" size="lg" type="button" onClick={onBack}>← Back</Button>
          <Button variant="green" size="lg" type="submit">Confirm appointment →</Button>
        </div>
      </form>
    </main>
  );
}

function methodText(s: BookingWizardState): string {
  if (s.method === 'inPerson') return 'In Person';
  if (s.method === 'video') return 'Video (Microsoft Teams)';
  if (s.method === 'phone') return 'Phone';
  return '—';
}
